/**
 * Vendor/Supplier Types
 * Shared between backend-wms, web-wms, and mobile-app
 *
 * Includes types for:
 * - Vendor classification and lifecycle
 * - Business terms and payment configuration
 * - Billing, shipping, and contact information
 * - Vendor preferences and certifications
 * - Performance metrics
 *
 * DRY Compliance: Single source for all vendor-related enums
 * Rule 15 Compliance: Proper type definitions
 */

// ============================================================================
// VENDOR CLASSIFICATION ENUMS
// ============================================================================

/**
 * Vendor type classification
 */
export enum VendorType {
  MANUFACTURER = 'MANUFACTURER',
  DISTRIBUTOR = 'DISTRIBUTOR',
  WHOLESALER = 'WHOLESALER',
  SERVICE_PROVIDER = 'SERVICE_PROVIDER',
  CARRIER = 'CARRIER',
  THREE_PL = '3PL',
}

/**
 * Vendor lifecycle status
 */
export enum VendorStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  BLACKLISTED = 'BLACKLISTED',
  PENDING = 'PENDING',
}

/**
 * Standard payment terms
 */
export enum PaymentTerms {
  COD = 'COD', // Cash on Delivery
  NET_15 = 'NET_15', // Net 15 days
  NET_30 = 'NET_30', // Net 30 days
  NET_45 = 'NET_45', // Net 45 days
  NET_60 = 'NET_60', // Net 60 days
  NET_90 = 'NET_90', // Net 90 days
  PREPAID = 'PREPAID', // Prepaid
  EOM = 'EOM', // End of Month
  MFI = 'MFI', // Month Following Invoice
}

// ============================================================================
// VENDOR INTERFACES
// ============================================================================

/**
 * Vendor billing information
 */
export interface VendorBillingInfo {
  billingEmail?: string;
  billingPhone?: string;
  billingAddress?: string;
  billingCity?: string;
  billingState?: string;
  billingPostalCode?: string;
  billingCountry?: string;
  bankName?: string;
  bankAccount?: string;
  bankRoutingNumber?: string;
  swiftCode?: string;
  iban?: string;
}

/**
 * Vendor shipping information
 */
export interface VendorShippingInfo {
  shippingContact?: string;
  shippingEmail?: string;
  shippingPhone?: string;
  defaultShippingMethod?: string;
  preferredCarrier?: string;
  leadTimeDays?: number;
  shippingTerms?: string; // FOB, CIF, etc.
}

/**
 * Vendor preferences
 */
export interface VendorPreferences {
  autoApproveOrders?: boolean;
  requireASN?: boolean;
  requirePOAcknowledgment?: boolean;
  preferredCommunication?: 'EMAIL' | 'PHONE' | 'PORTAL';
  documentFormat?: 'PDF' | 'EDI' | 'XML';
  labelFormat?: 'PDF' | 'ZPL' | 'EPL';
  languageCode?: string;
  timezone?: string;
  currency?: string;
}

/**
 * Vendor certification
 */
export interface VendorCertification {
  name: string;
  issuer?: string;
  certificateNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  status: 'ACTIVE' | 'EXPIRED' | 'PENDING_RENEWAL' | 'SUSPENDED';
  documentUrl?: string;
}

/**
 * Vendor performance metrics
 */
export interface VendorPerformanceMetrics {
  onTimeDeliveryRate?: number; // 0-100
  qualityScore?: number; // 0-100
  fillRate?: number; // 0-100
  returnRate?: number; // 0-100
  averageLeadTimeDays?: number;
  totalOrders?: number;
  totalValue?: number;
  lastOrderDate?: string;
}

/**
 * Vendor contact person
 */
export interface VendorContactPerson {
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  isPrimary?: boolean;
}

// ============================================================================
// UI LABELS & COLORS
// ============================================================================

/**
 * Vendor type labels for UI
 */
export const VendorTypeLabels: Record<VendorType, string> = {
  [VendorType.MANUFACTURER]: 'Manufacturer',
  [VendorType.DISTRIBUTOR]: 'Distributor',
  [VendorType.WHOLESALER]: 'Wholesaler',
  [VendorType.SERVICE_PROVIDER]: 'Service Provider',
  [VendorType.CARRIER]: 'Carrier',
  [VendorType.THREE_PL]: '3PL',
};

/**
 * Vendor status labels for UI
 */
export const VendorStatusLabels: Record<VendorStatus, string> = {
  [VendorStatus.ACTIVE]: 'Active',
  [VendorStatus.INACTIVE]: 'Inactive',
  [VendorStatus.BLACKLISTED]: 'Blacklisted',
  [VendorStatus.PENDING]: 'Pending',
};

/**
 * Vendor status colors for UI (Chakra UI color palette)
 */
export const VendorStatusColors: Record<VendorStatus, string> = {
  [VendorStatus.ACTIVE]: 'green',
  [VendorStatus.INACTIVE]: 'gray',
  [VendorStatus.BLACKLISTED]: 'red',
  [VendorStatus.PENDING]: 'yellow',
};

/**
 * Payment terms labels for UI
 */
export const PaymentTermsLabels: Record<PaymentTerms, string> = {
  [PaymentTerms.COD]: 'Cash on Delivery',
  [PaymentTerms.NET_15]: 'Net 15 Days',
  [PaymentTerms.NET_30]: 'Net 30 Days',
  [PaymentTerms.NET_45]: 'Net 45 Days',
  [PaymentTerms.NET_60]: 'Net 60 Days',
  [PaymentTerms.NET_90]: 'Net 90 Days',
  [PaymentTerms.PREPAID]: 'Prepaid',
  [PaymentTerms.EOM]: 'End of Month',
  [PaymentTerms.MFI]: 'Month Following Invoice',
};

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Vendor type values for validation
 */
export const VENDOR_TYPE_VALUES = [
  'MANUFACTURER',
  'DISTRIBUTOR',
  'WHOLESALER',
  'SERVICE_PROVIDER',
  'CARRIER',
  '3PL',
] as const;

/**
 * Vendor status values for validation
 */
export const VENDOR_STATUS_VALUES = ['ACTIVE', 'INACTIVE', 'BLACKLISTED', 'PENDING'] as const;

/**
 * Payment terms values for validation
 */
export const PAYMENT_TERMS_VALUES = [
  'COD',
  'NET_15',
  'NET_30',
  'NET_45',
  'NET_60',
  'NET_90',
  'PREPAID',
  'EOM',
  'MFI',
] as const;

/**
 * Default payment terms for new vendors
 */
export const DEFAULT_PAYMENT_TERMS = PaymentTerms.NET_30;

/**
 * New vendor period in days (for QC rules)
 */
export const NEW_VENDOR_PERIOD_DAYS = 90;
